import { useState, useEffect } from "react"
import { toast } from "sonner"
import { supabase } from "../lib/supabase"
import { placesByRaceDay, raceDayPlaceKey } from "../lib/pointsStandings"
import { Event, EventCategory, Driver, TournamentStats } from "../components/tournament/types"

export function useTournamentManagement() {
  const [events, setEvents] = useState<Event[]>([])
  const [selectedEvent, setSelectedEvent] = useState<string>("")
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [categories, setCategories] = useState<EventCategory[]>([])
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [activeTab, setActiveTab] = useState<"brackets" | "leaderboard">("brackets")
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<TournamentStats>({
    activeEvents: 0,
    totalDrivers: 0,
    liveBrackets: 0,
    totalPrizePool: "$0"
  })

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return ""
    const [year, month, day] = dateString.split("-")
    const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const isEventInPast = (eventDate: string): boolean => {
    if (!eventDate) return false
    const today = new Date()
    const [year, month, day] = eventDate.split("-")
    const eventDate_ = new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
    return eventDate_ < today
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (selectedEvent) {
      fetchCategories(selectedEvent)
    } else {
      setCategories([])
      setSelectedCategory("")
    }
  }, [selectedEvent])

  const fetchData = async () => {
    setLoading(true)
    try {
      // First, get all track_images that are drag racing events
      const { data: dragTrackImages, error: trackImagesError } = await supabase
        .from('track_images')
        .select(`
          id,
          title,
          track_type,
          event_dates(count)
        `)
        .eq('track_type', 'drag')
        .eq('is_event_flyer', true)

      if (trackImagesError) throw trackImagesError

      console.log('Drag track images found:', dragTrackImages)

      // Get all drag track image IDs (both single and multi-date events)
      const dragTrackImageIds = (dragTrackImages || []).map(ti => ti.id)

      if (dragTrackImageIds.length === 0) {
        console.log('No drag racing events found')
        setEvents([])
        setStats({
          activeEvents: 0,
          totalDrivers: 0,
          liveBrackets: 0,
          totalPrizePool: "$0"
        })
        setLoading(false)
        return
      }

      // Fetch events that belong to these drag track_images
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select(`
          *,
          event_registrations!event_registrations_event_id_fkey(count),
          track_images!inner(
            id,
            title,
            track_type,
            event_dates(
              id,
              event_date
            )
          )
        `)
        .in('track_image_id', dragTrackImageIds)
        .order('event_date', { ascending: false })

      if (eventsError) throw eventsError

      console.log('Events data found:', eventsData)

      // Transform events data
      const transformedEvents: Event[] = (eventsData || []).map(event => {
        const participants = event.event_registrations?.[0]?.count || 0
        const status = isEventInPast(event.event_date) ? "completed" : 
                     new Date(event.event_date) <= new Date() ? "live" : "upcoming"
        
        // Get all dates for this series
        const eventDates = event.track_images?.event_dates || []
        const sortedDates = eventDates
          .map((ed: { event_date: string }) => ed.event_date)
          .sort()
        
        return {
          id: event.id,
          name: event.name,
          event_date: event.event_date,
          is_active: event.is_active,
          status,
          participants,
          location: "Thunder Valley Dragstrip", // You can add location field to events table
          series_id: event.track_images?.id, // Use track_image_id as series_id
          event_dates: sortedDates,
        }
      })

      console.log('Transformed events:', transformedEvents)
      setEvents(transformedEvents)

      // Fetch driver statistics
      await fetchDriverStats()

      // Calculate stats
      const activeEvents = transformedEvents.filter(e => e.is_active).length
      const totalDrivers = transformedEvents.reduce((sum, e) => sum + e.participants, 0)
      const liveBrackets = transformedEvents.filter(e => e.status === "live").length
      const totalPrizePool = "$35K" // You can calculate this from categories

      setStats({
        activeEvents,
        totalDrivers,
        liveBrackets,
        totalPrizePool
      })

    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async (eventId: string) => {
    try {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('event_categories')
        .select(`
          *,
          event_registrations(count),
          tournament_brackets(
            id,
            status,
            current_round,
            total_rounds
          )
        `)
        .eq('event_id', eventId)
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (categoriesError) throw categoriesError

      // Transform categories data
      const transformedCategories: EventCategory[] = (categoriesData || []).map(category => {
        const participants = category.event_registrations?.[0]?.count || 0
        const bracket = category.tournament_brackets?.[0]
        
        let bracketStatus: EventCategory['bracket_status'] = "not_started"
        let currentRound = 0
        let totalRounds = 0

        if (bracket) {
          if (bracket.status === "completed") {
            bracketStatus = "completed"
          } else if (bracket.status === "active") {
            bracketStatus = "elimination_1"
          }
          currentRound = bracket.current_round || 0
          totalRounds = bracket.total_rounds || 0
        }

        const prizePool = category.first_place_prize && category.second_place_prize 
          ? `$${(category.first_place_prize + category.second_place_prize).toLocaleString()}`
          : "$0"

        return {
          id: category.id,
          name: category.name,
          vehicle_type: category.vehicle_type,
          event_id: category.event_id,
          prize_pool: prizePool,
          participants,
          max_participants: 32, // You can add this field to categories
          bracket_status: bracketStatus,
          current_round: currentRound,
          total_rounds: totalRounds,
        }
      })

      setCategories(transformedCategories)
    } catch (error) {
      console.error('Error fetching categories:', error)
      toast.error('Failed to load categories')
    }
  }

  const fetchDriverStats = async () => {
    try {
      // The points view already carries driver names, race numbers and the bonus awards,
      // so the all-time standings are a straight sum of its rows per driver
      const { data, error } = await supabase
        .from('event_driver_points')
        .select('driver_key, driver_name, race_number, event_id, event_date, category_name, total_points, best_reaction_time');

      if (error) throw error

      // Best finish and wins come from the same points-ranked places the public leaderboard shows
      const places = placesByRaceDay(data || []);

      const driverMap = new Map<string, Driver & { best_finish: number; eventDates: Set<string> }>();

      for (const row of data || []) {
        let driver = driverMap.get(row.driver_key);

        if (!driver) {
          driver = {
            id: row.driver_key,
            name: row.driver_name || 'Unknown Driver',
            race_number: row.race_number || '',
            total_points: 0,
            events_participated: 0,
            wins: 0,
            best_et: 0, // Elapsed times are not recorded yet
            best_rt: 0,
            best_finish: Infinity,
            eventDates: new Set<string>()
          };
          driverMap.set(row.driver_key, driver);
        }

        driver.total_points += row.total_points || 0;
        if (row.event_date) driver.eventDates.add(row.event_date);
        const place = places.get(raceDayPlaceKey(row));
        if (place === 1) driver.wins += 1;
        if (place && place < driver.best_finish) {
          driver.best_finish = place;
        }

        const reactionTime = row.best_reaction_time === null ? null : Number(row.best_reaction_time);
        if (reactionTime !== null && (driver.best_rt === 0 || reactionTime < driver.best_rt)) {
          driver.best_rt = reactionTime;
        }
      }

      const mappedDrivers = Array.from(driverMap.values()).map(({ eventDates, ...driver }) => ({
        ...driver,
        events_participated: eventDates.size,
        best_finish: driver.best_finish === Infinity ? 0 : driver.best_finish
      }));

      // Sort by total points (highest first), then by best finish (lowest first)
      mappedDrivers.sort((a, b) => {
        if (b.total_points !== a.total_points) {
          return b.total_points - a.total_points;
        }
        if (a.best_finish !== b.best_finish) {
          return a.best_finish - b.best_finish;
        }
        return a.name.localeCompare(b.name);
      });

      setDrivers(mappedDrivers);
    } catch (error) {
      console.error('Error fetching driver stats:', error)
    }
  }

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase())
    // Show all events (completed, live, and upcoming)
    return matchesSearch
  })

  const getSelectedEvent = () => events.find((e) => e.id === selectedEvent)
  const getSelectedCategory = () => categories.find((c) => c.id === selectedCategory)

  const getBracketStatusColor = (status: string) => {
    switch (status) {
      case "not_started":
        return `text-gray-400 border-gray-600`
      case "elimination_1":
        return `text-blue-400 border-blue-600`
      case "elimination_2":
        return `text-purple-400 border-purple-600`
      case "re_entry":
        return `text-amber-400 border-amber-600`
      case "completed":
        return `text-green-400 border-green-600`
      default:
        return `text-gray-400 border-gray-600`
    }
  }

  const getBracketStatusLabel = (status: string) => {
    switch (status) {
      case "not_started":
        return "Not Started"
      case "elimination_1":
        return "Round 1 Elimination"
      case "elimination_2":
        return "Round 2 Elimination"
      case "re_entry":
        return "Re-Entry Round"
      case "completed":
        return "Completed"
      default:
        return "Unknown"
    }
  }

  return {
    // State
    events,
    selectedEvent,
    selectedCategory,
    categories,
    drivers,
    activeTab,
    searchTerm,
    loading,
    stats,
    filteredEvents,
    
    // Actions
    setSelectedEvent,
    setSelectedCategory,
    setActiveTab,
    setSearchTerm,
    fetchData,
    fetchCategories,
    
    // Utilities
    formatDateForDisplay,
    isEventInPast,
    getSelectedEvent,
    getSelectedCategory,
    getBracketStatusColor,
    getBracketStatusLabel,
  }
} 